/* eslint-disable @typescript-eslint/no-unused-vars */
import { Inject, Injectable } from '@angular/core';
import {
  CategoryRepository,
  Product,
  ProductRepository,
  ShopPrice,
  Variant,
  VariantRepository,
  Where
} from '@infrab4a/connect';
import { Papa } from 'ngx-papaparse';
import { MessageService } from 'primeng/api';
import { lastValueFrom, map } from 'rxjs';
import { UserControllerService } from 'src/app/admin-api';
import { ProductBatchUpdateJSON } from 'src/app/connect-api/models/batch/ProductBatchUpdateJSON';
import { slugify } from 'src/app/utils/slug.util';
import { BaseConnectService } from '../base-connect.service';
import { ShopLoggingService } from '../shop-log-service';
import { ShopProductGroupService } from '../shop-product-group.service';
import { BatchService } from './batch.service';

@Injectable({
  providedIn: 'root'
})
export class ProductsBatchService implements BatchService {
  itemsForUpload: Partial<Product>[];
  chunkSize = 100;
  completeCount = 0;
  errors = [];
  validGroups: string[] = [];
  importLogProduct = [];

  constructor(
    private papa: Papa,
    @Inject('ProductRepository') private repository: ProductRepository,
    @Inject('VariantRepository') private variantRepository: VariantRepository,
    @Inject('CategoryRepository')
    private categoryRepository: CategoryRepository,
    private messageService: MessageService,
    private productGroupService: ShopProductGroupService,
    private userService: UserControllerService,
    private shopLoggingService: ShopLoggingService
  ) {
    this.loadValidGroups();
  }

  private async loadValidGroups(): Promise<void> {
    try {
      const response = await this.productGroupService.getList();

      this.validGroups = response.map((group) => group.id) || [];
    } catch (error) {
      console.error('Erro ao carregar grupos do Hooklab:', error);
      // Fallback para valores padrão caso ocorra erro
      this.validGroups = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'];
    }
  }

  async parse(input: File): Promise<Partial<Product>[]> {
    if (this.validGroups.length === 0) {
      await this.loadValidGroups();
    }

    return new Promise<Partial<Product>[]>((resolve, reject) => {
      if (!input) return resolve((this.itemsForUpload = []));

      this.papa.parse(input, {
        header: true,
        dynamicTyping: true,
        skipEmptyLines: true,
        complete: async (results) => {
          try {
            if (results.errors.length > 0) throw results.errors.shift();

            this.itemsForUpload = await Promise.all(
              results.data
                .filter((c) => Object.keys(c).some((k) => c[k]))
                .map(
                  async (product: ProductBatchUpdateJSON, index) =>
                    await this.convertToProduct(product, index)
                )
            );

            resolve(this.itemsForUpload);
          } catch (error) {
            reject(new Error(`Arquivo inválido: ${error.message}`));
          }
        }
      });
    });
  }

  async update(): Promise<Array<Partial<Product>>> {
    if (this.itemsForUpload?.length <= 0) {
      return [];
    }

    this.errors = [];

    const chunks = this.getChunkedProductList();
    return await this.startImportation(chunks);
  }

  get importProgress(): number {
    return +((this.completeCount * 100) / this.itemsForUpload.length).toFixed(
      0
    );
  }

  private async convertToProduct(
    product: ProductBatchUpdateJSON,
    index: number
  ): Promise<Partial<Product>> {
    this.validateProduct(product, index);
    const hasAnyMetadata = product.metadataDescription || product.metadataTitle;
    const categoryId: string = await this.searchCategoryReference(
      product.category_reference,
      index
    );

    return Object.entries({
      EAN: product.EAN.toString(),
      price: this.buildProductPrices(product, index),
      ...('published' in product &&
        [true, false].includes(product.published) && {
          published: product.published
        }),
      ...(product.description ||
      product.differentials ||
      product.whoMustUse ||
      product.howToUse ||
      product.brandDescription ||
      product.ingredients ||
      product.purpose
        ? {
            description: {
              ...(product.description
                ? { description: product.description }
                : {}),
              ...(product.differentials
                ? { differentials: product.differentials }
                : {}),
              ...(product.whoMustUse ? { whoMustUse: product.whoMustUse } : {}),
              ...(product.howToUse ? { howToUse: product.howToUse } : {}),
              ...(product.brandDescription
                ? { brand: product.brandDescription }
                : {}),
              ...(product.ingredients
                ? { ingredients: product.ingredients }
                : {}),
              ...(product.purpose ? { purpose: product.purpose } : {})
            }
          }
        : {}),
      ...(hasAnyMetadata && {
        metadata: {
          ...(product.metadataDescription
            ? { description: product.metadataDescription }
            : {}),
          ...(product.metadataTitle ? { title: product.metadataTitle } : {})
        }
      }),
      ...(product.type && { type: product.type }),
      ...('tagsCollection' in product && {
        tagsCollection: this.getCollectionTags(product)
      }),
      ...('tagsProfile' in product && {
        tagsProfile: this.getProfileTags(product)
      }),
      ...('filters' in product && {
        filters: this.getFilters(product)
      }),
      ...(product.brand && { brand: product.brand.trim() }),
      ...(product.weight && { weight: product.weight }),
      ...(product.name && { name: product.name.trim() }),
      ...(product.slug && { slug: slugify(product.slug) }),
      ...(product.NCM && { NCM: product.NCM.toString() }),
      ...(product.sku && { sku: product.sku.toString().trim() }),
      ...(product.CEST && { CEST: product.CEST.toString() }),
      ...(product.stock && { stock: { quantity: product.stock } }),
      ...(product.video && { video: product.video }),
      ...(product.category_reference && { categoryId }),
      ...(product.label && { label: this.getLabel(product.label) }),
      ...(product.group && { groupId: product.group }),
      ...('validity' in product &&
        [true, false].includes(product.validity) && {
          validity: product.validity
        }),
      ...(product.daysOfUse && { daysOfUse: parseInt(product.daysOfUse) })
    }).reduce(
      (iProduct, [key, value]) =>
        ![undefined, null].includes(value)
          ? { ...iProduct, [key]: value }
          : iProduct,
      {}
    );
  }

  private validateProduct(product: ProductBatchUpdateJSON, index: number) {
    if (!product.EAN)
      throw new Error(`Produtos sem campo de EAN na linha ${index + 2}!`);

    const validLabels = [
      'outlet',
      'promocao',
      'ultimasunidades',
      'glamstar',
      'null'
    ];

    if (
      'label' in product &&
      !validLabels.includes(product.label.toLowerCase().trim())
    )
      throw new Error(
        `Label de produto inválido na linha: ${
          index + 2
        }. Os valores permitidos são: outlet, promocao, ultimasunidades, glamstar ou null`
      );

    if (
      'group' in product &&
      !this.validGroups.includes(product.group.toString())
    ) {
      const gruposPermitidos = this.validGroups.join(', ');
      throw new Error(
        `Grupo de produto inválido na linha: ${
          index + 2
        }. Os valores permitidos são: ${gruposPermitidos}`
      );
    }

    if ('validity' in product && ![true, false].includes(product.validity)) {
      throw new Error(
        `Vigência de produto inválida na linha: ${
          index + 2
        }. Os valores permitidos são: true, false`
      );
    }

    if ('published' in product && ![true, false].includes(product.published)) {
      throw new Error(
        `Campo 'published' de produto inválido na linha: ${
          index + 2
        }. Os valores permitidos são: true, false`
      );
    }

    if (
      'category_reference' in product &&
      String(product.category_reference).trim().length < 5
    )
      throw new Error(
        `Apenas categoria de nivel 3 podem ser associada a produtos. Erro na linha: ${
          index + 2
        }`
      );

    if ('daysOfUse' in product && !Number.isInteger(product.daysOfUse))
      throw new Error(
        `Apenas números inteiros podem ser inseridos no campo 'daysOfUse', não é permitido números com casas decimais. Erro na linha: ${
          index + 2
        }`
      );
  }

  private getLabel(input: string) {
    if (input.toLowerCase().trim() == 'promocao') return 'on-sale';
    if (input.toLowerCase().trim() == 'ultimasunidades') return 'last-units';
    if (input.toLowerCase().trim() == 'outlet' || input == 'glamstar')
      return input;
    if (input.toLowerCase().trim() == 'null') return BaseConnectService.NULL;
  }

  private async searchCategoryReference(reference: string, index: number) {
    if (!reference) return null;

    const category = await this.categoryRepository
      .find({
        filters: {
          reference: {
            operator: Where.EQUALS,
            value: reference.toString().trim()
          }
        }
      })
      .then((res) => res.data);

    if (!category.length)
      throw new Error(
        `Categoria do produto não encontrado na linha ${index + 2}!`
      );

    return category.at(0).id.toString();
  }

  private buildProductPrices(
    product: ProductBatchUpdateJSON,
    index: number
  ): ShopPrice {
    if (!(product.price || product.fullPrice || product.discountPercentage)) {
      return;
    }

    if (
      !this.hasValidPrices(
        product.price,
        product.fullPrice,
        product.discountPercentage
      )
    )
      throw new Error(
        `Para atualizar o preços, os campos de price, fullPrice e discountPercentage devem ser informados para o produto na linha ${
          index + 2
        }`
      );

    const hasInvalidCaracters =
      product.price?.toString().includes(',') ||
      product.fullPrice?.toString().includes(',') ||
      product.discountPercentage?.toString().includes(',');

    if (hasInvalidCaracters)
      throw new Error(
        `Produtos com preços fora do padrão na linha ${index + 2}!`
      );

    const subscriberPrice =
      product.price - (product.price * product.discountPercentage) / 100;
    const fullPriceDiscountPercentage =
      100 - (subscriberPrice / product.fullPrice) * 100;

    const prices = {
      ...(product.price && { price: +product.price.toFixed(2) }),
      ...(product.fullPrice && { fullPrice: +product.fullPrice.toFixed(2) }),
      ...(product.discountPercentage && {
        subscriberDiscountPercentage: product.discountPercentage,
        subscriberPrice: +subscriberPrice.toFixed(2),
        fullPriceDiscountPercentage: +fullPriceDiscountPercentage.toFixed(2)
      })
    };

    return prices;
  }

  private hasValidPrices(price, fullPrice, discountPercentage): boolean {
    if (
      (price && (!fullPrice || !discountPercentage)) ||
      (fullPrice && (!price || !discountPercentage)) ||
      (discountPercentage && (!fullPrice || !price))
    )
      return false;

    return true;
  }

  private async startImportation(
    chunks: Partial<Product>[][]
  ): Promise<Array<Partial<Product>>> {
    this.importLogProduct = [];
    let successImportCount = 0;
    this.completeCount = 0;
    const updated: Array<Partial<Product>> = [];
    for (const chunk of chunks) {
      for (const product of chunk) {
        try {
          const p = await this.updateProduct(product);
          if (p) {
            updated.push(p);
            successImportCount++;
          }
        } catch (error) {
          console.log(error);
          this.errors.push(error);
        } finally {
          this.completeCount++;
        }
      }
    }
    if (this.errors.length === this.completeCount) {
      this.messageService.add({
        severity: 'warn',
        detail: `Não foi possível realizar a atualização`,
        summary: 'Erro'
      });
    } else {
      await this.createLog(this.importLogProduct);

      this.messageService.add({
        severity: 'success',
        detail: ` Foram atualizados ${successImportCount} produtos`,
        summary: 'Atualização concluída!'
      });
    }

    return updated?.length ? updated : [];
  }

  private getChunkedProductList(): Partial<Product>[][] {
    const itemsForUpload = [...this.itemsForUpload];

    return [...Array(Math.ceil(itemsForUpload.length / this.chunkSize))].map(
      () => itemsForUpload.splice(0, this.chunkSize)
    );
  }

  private async updateProduct(
    data: Partial<Product>
  ): Promise<Partial<Product>> {
    const updates = [];
    const productVariante = await this.variantRepository
      .find({
        fields: ['id', 'EAN', 'sku', 'productId'],
        filters: { EAN: data.EAN },
        limits: { limit: 1 }
      })
      .then((res) => res.data.at(0));
    let productMain = await this.repository
      .find({
        fields: ['id', 'EAN', 'sku', 'name', 'brand', 'hasVariants'],
        filters: { EAN: data.EAN },
        limits: { limit: 1 }
      })
      .then((res) => res.data.at(0));

    if (productVariante) {
      const variant = Object.entries({
        price: data.price,
        ...(data.sku && { sku: data.sku.trim() }),
        ...(data.costPrice && { costPrice: data.costPrice }),
        ...(data.weight && { weight: data.weight }),
        ...(data.stock && { stock: data.stock }),
        ...(data.groupId && { groupId: data.groupId }),
        ...('tagsCollection' in data && {
          tagsCollection: data.tagsCollection
        }),
        ...('tagsProfile' in data && { tagsProfile: data.tagsProfile }),
        ...('daysOfUse' in data && { daysOfUse: data.daysOfUse }),
        ...('validity' in data && { validity: data.validity }),
        ...('published' in data && { published: data.published }),
        ...('filters' in data && { filters: data.filters }),
        ...('metadata' in data && { metadata: data.metadata }),
        ...('categoryId' in data && { categoryId: data.categoryId }),
        ...('label' in data && { label: data.label }),
        ...('description' in data && { description: data.description })
      }).reduce(
        (iProduct, [key, value]) =>
          ![undefined, null].includes(value)
            ? { ...iProduct, [key]: value }
            : iProduct,
        {}
      );

      this.importLogProduct.push(
        Variant.toInstance({
          id: productVariante.id,
          ...variant
        })
      );

      updates.push(
        this.variantRepository.update(
          Variant.toInstance({
            id: productVariante.id,
            ...variant
          })
        )
      );
    }

    if (productMain && !productVariante) {
      if (productMain.hasVariants) {
        const { price, tagsProfile, tagsCollection, groupId, ...updateData } =
          data;
        updates.push(
          this.repository.update(
            Product.toInstance({
              id: productMain?.id,
              ...updateData
            })
          )
        );

        this.importLogProduct.push(
          Product.toInstance({
            id: productMain?.id,
            ...updateData
          })
        );
      } else {
        updates.push(
          this.repository.update(
            Product.toInstance({
              id: productMain?.id,
              ...data
            })
          )
        );

        this.importLogProduct.push(
          Product.toInstance({
            id: productMain?.id,
            ...data
          })
        );
      }
    }

    if (!productMain && productVariante?.productId) {
      productMain = await this.repository
        .find({
          fields: ['id', 'EAN', 'sku', 'name', 'brand', 'hasVariants'],
          filters: { id: productVariante.productId }
        })
        .then((res) => res.data.shift());
    }

    if (!updates.length) {
      throw { message: `EAN ${data.EAN} não encontrado.` };
    }

    await Promise.all(updates);

    return {
      sku: productVariante?.sku ?? productMain?.sku,
      EAN: productVariante?.EAN ?? productMain?.EAN,
      name: productMain.name,
      brand: productMain.brand
    };
  }

  private getCollectionTags(product: ProductBatchUpdateJSON) {
    if ('tagsCollection' in product && product.tagsCollection) {
      const tags = product.tagsCollection
        .toLowerCase()
        .split(';')
        .map((tag) => slugify(tag.trim()))
        .filter((tag) => tag !== 'null')
        .filter(Boolean);

      return [...new Set(tags)];
    }

    if ('tagsCollection' in product && !product.tagsCollection)
      return BaseConnectService.NULL;

    return null;
  }

  private getProfileTags(product: ProductBatchUpdateJSON) {
    if ('tagsProfile' in product && product.tagsProfile) {
      const tags = product.tagsProfile
        .toLowerCase()
        .split(';')
        .map((tag) => slugify(tag.trim()))
        .filter((tag) => tag !== 'null')
        .filter(Boolean);

      return [...new Set(tags)];
    }

    if ('tagsProfile' in product && !product.tagsProfile)
      return BaseConnectService.NULL;

    return null;
  }

  private getFilters(product: ProductBatchUpdateJSON) {
    if ('filters' in product && product.filters) {
      const filters = product.filters
        .toLowerCase()
        .split(';')
        .map((filter) => filter.trim())
        .filter((filter) => filter != '');

      return [...new Set(filters)];
    }

    if ('filters' in product && !product.filters)
      return BaseConnectService.NULL;

    return null;
  }

  async createLog(documents: any[]) {
    const user = await lastValueFrom(
      this.userService.getInfo().pipe(map((data) => data.result))
    );

    return this.shopLoggingService.createBatchUpdateLog('products', documents, {
      id: user.userId.toString(),
      name: user.displayName,
      email: user.username
    });
  }
}
